import { Canceler } from 'axios';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { FixedTab, RouteTabs, VariableTab } from '../../components/RouteTabs';
import useBackdrop from '../../hooks/useBackdrop';
import useConfirmDialog from '../../hooks/useConfirmDialog';
import useSnackbar from '../../hooks/useSnackbar';
import { getAbtestList } from '../../services/api/Abtest';
import api from '../../services/api/base';
import { IABTest, IABTestListOption } from '../../services/types';
import { RouteNames } from '../config';
import ABTestCreate from './ABTestCreate';
import ABTestList from './ABTestList';

interface IProps {
  tabIndex?: number;
}
interface IParamProps {
  testId?: string;
}

const Abtest = (props: IProps): JSX.Element => {
  const path = RouteNames.Abtest;
  const match = useRouteMatch<IParamProps>(`${path}/v/:testId`);
  const testId = match?.params?.testId;

  const [abTests, setAbTests] = useState<IABTest[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [limit, setLimit] = useState<number>(20);
  const [listOption, setListOption] = useState<IABTestListOption>({});
  const initialTestIds = testId ? [Number(testId)] : [];
  const [editingTestIds, setEditingTestIds] = useState<number[]>(initialTestIds);

  const confirm = useConfirmDialog();
  const backdrop = useBackdrop();
  const snackbar = useSnackbar();
  const canceler = useRef<Canceler>(() => undefined);
  const history = useHistory();

  const onError = useCallback(
    e => {
      snackbar.error(`An error has occurred. ${e.message}`);
    },
    [snackbar],
  );

  useEffect(() => {
    // const fetchData = async () => {
    //   canceler.current();

    //   try {
    //     const [fetchPromise, cancel] = await getAbtestList({ page: 0, limit: 20 });
    //     canceler.current = cancel;
    //     const result = await fetchPromise;

    //     setAbTests(result.data.items);
    //     setTotalCount(result.data.pageSize);
    //   } catch (e) {
    //     if (api.isCancel(e)) return;
    //   }
    // };

    fetchList(0, 20);

    // return () => canceler.current();
  }, [listOption]);

  const fetchList = async (page: number, limit: number) => {
    canceler.current();

    const params = {
      page: page + 1,
      limit,
      testType: listOption.testType === 'all' ? undefined : listOption.testType,
      isActive: listOption.activeOnly || undefined,
    };

    try {
      const [fetchPromise, cancel] = await getAbtestList(params);
      canceler.current = cancel;
      const result = await fetchPromise;

      setAbTests(result.data.items);
      setTotalCount(result.data.pageSize);
    } catch (e) {
      if (api.isCancel(e)) return;
    }
  };

  const handleCreate = () => {
    history.push(RouteNames.Abtest);
    fetchList(0, 20);
  };

  const handleDelete = (testId: number) => {
    setEditingTestIds(editingTestIds.filter(id => id !== testId));
    fetchList(0, 20);
  };

  const handleTabClose = useCallback(
    testId => {
      setEditingTestIds(editingTestIds.filter(id => id !== testId));
      history.push(`${RouteNames.Abtest}/list`);
    },
    [editingTestIds],
  );

  const handleRowClick = useCallback(
    (testId: number | null) => {
      if (testId !== null && !editingTestIds.find(id => id === testId)) {
        setEditingTestIds([...editingTestIds, testId]);
      }
    },
    [editingTestIds],
  );

  const handleChangeListOption = (options: IABTestListOption) => {
    setListOption(options);
  };

  const getTabLabel = useCallback(testId => abTests.find(s => s.id === testId)?.testName || `View ${testId}`, [abTests]);

  const getTabUri = useCallback(testId => {
    return `v/${testId}`;
  }, []);

  const keyExtractor = useCallback(testId => `tab-floating-${testId}`, []);

  const variableTabRenderer = useCallback(
    testId => <ABTestCreate testId={testId} onCreated={handleCreate} onDeleted={() => handleDelete(testId)} />,
    [handleCreate],
  );

  return (
    <div>
      <RouteTabs path={path}>
        <FixedTab label="All Tests" uri="list">
          <ABTestList
            abTests={abTests}
            listOption={listOption}
            onClickRow={handleRowClick}
            onRequestFetch={fetchList}
            totalCount={totalCount}
            onChangeListOption={handleChangeListOption}
          />
        </FixedTab>
        <FixedTab label="Create a new test" uri="create">
          <ABTestCreate onCreated={handleCreate} />
        </FixedTab>
        <VariableTab<number>
          data={editingTestIds}
          label={getTabLabel}
          uri={getTabUri}
          onClose={handleTabClose}
          keyExtractor={keyExtractor}
          render={variableTabRenderer}
        />
      </RouteTabs>
    </div>
  );
};

export default Abtest;
